// ABOUTME: REST hook for the variant pricing reverse lookup (GET /api/cpq/variant-pricing/emitters).
// ABOUTME: Read-only debug call; returns the rules that can emit a given price key.

import { useCallback, useState } from 'react';

export type Bucket = 'hoist' | 'trolley' | 'pendant' | 'chainContainer';

export interface KeyEmitterMatch {
    procedureCode: string;
    ruleIndex: number;
    ruleType: 'emit' | 'tableEmit';
    bucket?: Bucket;
    tableRef?: string;
    matchedRowKeys?: Record<string, string>;
    keyExpression?: string;
    matchedLiterals?: string[];
    models: string[];
}

export interface KeyEmittersResult {
    key: string;
    pricedInPriceBook: boolean;
    pricedForModels: string[];
    // Decision-table rows that emit the key literally — high confidence.
    exactMatches: KeyEmitterMatch[];
    // emit rules whose template could produce it — low confidence, ranked by match count.
    templateCandidates: KeyEmitterMatch[];
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

/**
 * Hook for the read-only "which rules emit this key?" endpoint. Requires the CPQ_PRICING_DEBUG role
 * server-side; without it the call answers 403 and the hook surfaces the error message.
 */
export function useKeyEmitters() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<KeyEmittersResult | null>(null);

    const search = useCallback(async (key: string) => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await fetch(
                `/api/cpq/variant-pricing/emitters?key=${encodeURIComponent(key)}`,
                { headers: { 'Content-Type': 'application/json' } },
            );
            const json: ApiResponse<KeyEmittersResult> = await response.json();
            if (!json.success) {
                setError(json.error ?? 'Unknown error');
                return null;
            }
            setResult(json.data as KeyEmittersResult);
            return json.data as KeyEmittersResult;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to look up key');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, result, search };
}
